import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { SectionHead } from "@/components/SectionHead";

const EFFECTIVE = "5 September 2026";
// Fill in before publishing the store listings.
const CONTACT = "[contact email]";

function Prose({ children }: { children: ReactNode }) {
  return (
    <section className="container-narrow" style={{ paddingBottom: "var(--s-9)" }}>
      <div className="t-display legal-prose">{children}</div>
    </section>
  );
}

export function Privacy() {
  return (
    <div className="page">
      <SectionHead kicker={`Privacy policy · effective ${EFFECTIVE}`} title="What LEX · 30 knows about you" narrow />
      <Prose>
        <p>
          LEX · 30 is a vocabulary almanac. It is <strong>local-first</strong>: everything you do — the words you mark,
          the cards you grade, your daily counts and streak, your settings — is stored on your own device (in the
          browser's storage on the web, in the app's database on a phone). Nothing leaves the device unless you sign in.
        </p>

        <h2>If you sign in with Google</h2>
        <p>
          Signing in exists for one purpose: carrying your progress between your devices. When you sign in we receive
          from Google your account identifier, e-mail address, display name and profile picture, and we store them in
          our database together with your progress records: the review schedule for each word, and per-day study
          counters keyed by an anonymous per-device identifier. Nothing else is collected — no page views, no
          analytics, no advertising identifiers, no location.
        </p>
        <p>
          The database runs on Cloudflare's network (Cloudflare D1); Google provides the sign-in. Neither receives your
          progress for its own purposes. We do not sell, rent or share your data with anyone.
        </p>

        <h2>Audio</h2>
        <p>
          When the pronunciation source is set to Youdao, the app requests the recording of the word you are looking
          at from Youdao's dictionary service, which therefore sees that word and your network address, like any web
          request. Recordings are cached on your device afterwards. The device text-to-speech option never leaves the
          device. You can switch the source or turn audio off in Settings.
        </p>

        <h2>Notifications</h2>
        <p>
          Reminders are scheduled on the device itself. There is no push server and no notification token is
          collected.
        </p>

        <h2>Keeping and deleting</h2>
        <p>
          Synced progress is kept for as long as your account exists. <strong>Delete account</strong> in Settings
          removes the account and every synced record immediately; your device keeps its local copy. <strong>Reset
          progress</strong> erases the local copy (and, while signed in, the synced one). <strong>Export</strong> gives
          you a JSON file of everything at any time.
        </p>

        <h2>Children</h2>
        <p>LEX · 30 is not directed at children under 13 and does not knowingly collect information from them.</p>

        <h2>Changes and contact</h2>
        <p>
          If this policy changes, the effective date above changes with it and the app's release notes say so.
          Questions: {CONTACT}. See also the <Link to="/terms">terms of use</Link>.
        </p>
      </Prose>
    </div>
  );
}

export function Terms() {
  return (
    <div className="page">
      <SectionHead kicker={`Terms of use · effective ${EFFECTIVE}`} title="The short version" narrow />
      <Prose>
        <p>
          LEX · 30 is offered for personal study. You may use it, on the web and in the apps, to learn the vocabulary
          it presents. The word list follows a published thirty-day TOEIC course; the entries, translations and example
          sentences remain the property of their respective rights holders and are provided for study, not for
          redistribution.
        </p>
        <h2>Your account</h2>
        <p>
          You are responsible for the Google account you sign in with. You can delete your LEX · 30 account at any
          time from Settings. We may remove accounts that abuse the service, for example by flooding the sync endpoint.
        </p>
        <h2>No warranty</h2>
        <p>
          The service is provided as is, without warranty of any kind. Keep an export of progress that matters to you;
          the app makes that a single tap.
        </p>
        <h2>Changes</h2>
        <p>
          These terms may change; the effective date above will change with them. Continued use after a change means
          you accept it. Questions: {CONTACT}. Read the <Link to="/privacy">privacy policy</Link> for what we store.
        </p>
      </Prose>
    </div>
  );
}
